import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { HubClient } from '../utils/HubClient';
import { useHubCacheInvalidation } from '../hooks/useHubCacheInvalidation';
import useDeviceId from '../hooks/useDeviceId';
import config from '../config';

const HubCtx = createContext<HubClient | null>(null);

export function HubProvider({ locationId, url, children }: { locationId: number; url: string; children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const deviceId = useDeviceId();

  // Keep these in refs to prevent the socket from being recreated on focus
  const getAccessTokenRef = useRef(getAccessToken);
  const locationIdRef = useRef(locationId);
  const deviceIdRef = useRef(deviceId);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);
  useEffect(() => {
    locationIdRef.current = locationId;
  }, [locationId]);
  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  const getAuth = useCallback(async () => {
    const jwt = await getAccessTokenRef.current();
    if (!jwt) throw new Error('No token');
    return {
      jwt,
      locationId: locationIdRef.current,
      deviceId: deviceIdRef.current,
      clientVersion: config.release.toString(),
      clientApp: 'EPOS',
    };
  }, []);

  const client = useMemo(() => new HubClient(url, getAuth), [url, getAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    client.connect();

    return () => {
      client.close();
    };
  }, [client]);

  useHubCacheInvalidation(client);

  return <HubCtx.Provider value={client}>{children}</HubCtx.Provider>;
}

export const useHub = () => {
  const c = useContext(HubCtx);
  if (!c) throw new Error('HubProvider missing');
  return c;
};
